import { RecordStrategy } from "../interfaces/record-strategy.interface";
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import RecordRTC from 'recordrtc';
import { Injectable, NgZone, signal } from "@angular/core";
import { ChatMessageType, MediaRecordStatus } from "../../common/models";
import { BaseRecordStrategy } from "./base-record-strategy";
import { Timer } from "../utils/record-timer";
@Injectable()
export class NativeRecordStrategy extends BaseRecordStrategy implements RecordStrategy {
  private mediaElement: HTMLMediaElement;
  private type: ChatMessageType;

  private recordRTC: RecordRTC = null;
  private stream: MediaStream;
  private timer: Timer = null;

  deviceReady = signal(false);
  timestamp = signal(0);
  status = signal<MediaRecordStatus>(MediaRecordStatus.INIT);

  private readonly maxDuration = 300;

  constructor(sanitizer: DomSanitizer, private readonly toastr: ToastrService, private readonly ngZone: NgZone) {
    super(sanitizer);
  }

  async init(mediaElement: HTMLMediaElement, type: ChatMessageType): Promise<void> {
    if (!mediaElement || !type) {
      return;
    }

    this.mediaElement = mediaElement;
    this.type = type;

    try {
      const kb = 1024, bps = 800 * kb;
      const constraints: MediaStreamConstraints = { audio: true, video: { width: 1080, height: 840 } };
      const isVideo = this.type === ChatMessageType.VIDEO_MESSAGE;

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      const video = this.mediaElement as HTMLVideoElement;
      video.srcObject = this.stream;
      video.muted = true;
      await video.play();

      this.recordRTC = new (RecordRTC as RecordRTC)(this.stream, {
        type: isVideo ? 'video' : 'audio',
        mimeType: isVideo ? 'video/mp4' : 'audio/wav',
        disableLogs: true,
        bitsPerSecond: bps,
        audioBitsPerSecond: 100 * kb,
        videoBitsPerSecond: isVideo ? bps : undefined,
        timeSlice: 500
      });

      this.deviceReady.set(true);
    } catch (error) {
      this.toastr.error(error);
    }
  }

  async start(): Promise<void> {
    if (!this.recordRTC) await this.init(this.mediaElement, this.type);

    this.recordRTC.startRecording();
    this.status.set(MediaRecordStatus.RECORDING);
    this.startTimer();
  }

  async stop(): Promise<void> {
    this.status.set(MediaRecordStatus.FINISHED);
    await this.recordRTC.stopRecording(() => {
      this.stopTimer();

      const blob = this.recordRTC.getBlob();
      const name = `${Date.now()}.${blob.type.split('/')[1]}`;
      const recordedData = new File([blob], name, { type: blob.type, lastModified: Date.now() });

      this.setRecordedBlob(recordedData, this.type === ChatMessageType.AUDIO_MESSAGE);
    });
  }

  async pause(): Promise<void> {
    this.recordRTC.pauseRecording();
    this.timer.pause();
  }

  async resume(): Promise<void> {
    this.recordRTC.resumeRecording();
    this.timer.start();
  }

  async reset(): Promise<void> {
    if (!this.recordRTC) {
      return;
    }

    this.recordRTC.reset();
    this.status.set(MediaRecordStatus.INIT);
    this.timestamp.set(0);
    this.resetRecordedData();
  }

  destroy(): void {
    if (!this.recordRTC) {
      return;
    }

    this.recordRTC.destroy();
    this.recordRTC = null;
    this.stopTimer();

    this.stream.getTracks().forEach(track => track.stop());
  }

  private startTimer(): void {
    this.timer = new Timer((seconds) => {
      this.ngZone.run(() => {
        this.timestamp.set(seconds);
        if (this.maxDuration && this.timestamp() >= this.maxDuration) {
          this.stop();
        }
      });
    });

    this.timer.start();
  }

  private stopTimer(): void {
    if (!this.timer) {
      return;
    }

    this.timer.stop();
    this.timer = null;
  }
}
